/**
 * One data panel view as a cell of a layout's grid — a data layout
 * (docs/features/pip-and-data-panel.md, *Data layouts*; rules in
 * `lib/data-layout.ts`).
 *
 * A header names the view; ▾ picks another (each once per layout, Fin St once
 * per grain) or turns the cell into a chart; ⧉ opens the view alone in its own
 * window. Under it, the view for the chart the cell follows — handed in by the
 * caller, which knows the workspace.
 */

import { useMemo, useRef, useState } from "react";
import type { DataViewConfig } from "@qufolio/model";

import { Menu } from "./Menu";
import { SymbolDataView } from "./SymbolDataPanel";
import { symbolView, type SymbolView } from "./symbol-views";
import { FinStGrainContext, type FinStGrainStore } from "./fin-st-grain";
import { useViewDetach } from "./view-detach";
import { cellKey, DATA_VIEWS, type CellChoice } from "../lib/data-layout";

export interface DataViewCellProps {
  config: DataViewConfig;
  /** What the layout's other cells already show (`keysInUse`): greyed out in ▾. */
  inUse: ReadonlySet<string>;
  /** The followed chart's symbol; absent when there is no chart to follow yet. */
  symbol: string | undefined;
  chartGroupId: string | undefined;
  onChoose: (choice: CellChoice) => void;
  /** Point the followed chart at another symbol — a peer, say. */
  onSymbol?: ((symbol: string) => void) | undefined;
  /**
   * ▾'s **Follow** section, for the whole layout (`lib/data-layout.ts`
   * `setFollow`). Absent in a mixed layout, whose cells follow its own charts.
   */
  follow?:
    | {
        /** The layout followed; `undefined` is the chart clicked last. */
        current: string | undefined;
        /** Every layout with a chart, in tab order. */
        options: ReadonlyArray<{ id: string; name: string }>;
        onFollow: (layoutId: string | null) => void;
      }
    | undefined;
}

const GRAIN_LABEL = { years: "FY", quarters: "Qtr" } as const;

/** ▾'s rows: every view, Fin St once per grain. */
type Row = { choice: Exclude<CellChoice, "chart">; label: string };
const CHOICES: readonly Row[] = DATA_VIEWS.flatMap<Row>((view) =>
  view === "fundamentals"
    ? (["years", "quarters"] as const).map((finStGrain) => ({
        choice: { view, finStGrain },
        label: `${symbolView(view)?.label ?? view} · ${GRAIN_LABEL[finStGrain]}`,
      }))
    : [{ choice: { view }, label: symbolView(view)?.label ?? view }],
);

export function DataViewCell({ config, inUse, symbol, chartGroupId, onChoose, onSymbol, follow }: DataViewCellProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLButtonElement | null>(null);
  const viewDetach = useViewDetach();
  const mine = cellKey(config);
  const grain = config.finStGrain ?? "years";
  const label = config.view
    ? `${symbolView(config.view)?.label ?? config.view}${config.view === "fundamentals" ? ` · ${GRAIN_LABEL[grain]}` : ""}`
    : "Pick a view";

  // **A Fin St cell keeps its own grain**, not the chart's, so a layout can hold
  // Fin St by year beside Fin St by quarter (owner, 2026-10-29). The table reads
  // its grain from this context, so the cell answers for it here; switching the
  // grain in the table is switching the cell, and refused if the other grain's
  // cell is already there.
  const grainStore = useMemo<FinStGrainStore>(
    () => ({
      get: () => grain,
      set: (_chart, next) => onChoose({ view: "fundamentals", finStGrain: next }),
    }),
    [grain, onChoose],
  );

  const body = !config.view ? (
    <p style={NOTE}>Pick a view with ▾ above.</p>
  ) : !symbol ? (
    <p style={NOTE}>No chart to follow yet — click a chart in any window.</p>
  ) : (
    <SymbolDataView symbol={symbol} view={config.view as SymbolView["id"]} chartGroupId={chartGroupId} onSymbol={onSymbol} />
  );

  return (
    <div style={HOST}>
      <div style={HEAD}>
        <span style={TITLE}>{label}</span>
        <button
          ref={menuRef}
          type="button"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label="What this cell shows"
          title="What this cell shows"
          onClick={() => setMenuOpen((v) => !v)}
          style={ICON}
        >
          ▾
        </button>
        {viewDetach && config.view && (
          <button
            type="button"
            onClick={() => viewDetach.detach(config.view as SymbolView["id"])}
            aria-label={`Open ${label} in its own window`}
            title={`Open ${label} in its own window`}
            style={ICON}
          >
            ⧉
          </button>
        )}
      </div>
      {menuOpen && (
        <Menu anchorRef={menuRef} onClose={() => setMenuOpen(false)} align="right" title="Show in this cell">
          {CHOICES.map(({ choice, label: rowLabel }) => {
            const key = cellKey(choice)!;
            const current = key === mine;
            const taken = !current && inUse.has(key);
            return (
              <button
                key={key}
                type="button"
                disabled={taken}
                title={taken ? "Already in another cell of this layout" : undefined}
                onClick={() => {
                  setMenuOpen(false);
                  if (!current) onChoose(choice);
                }}
                style={{ ...ROW, ...(current ? ROW_ON : {}), ...(taken ? ROW_TAKEN : {}) }}
              >
                <span aria-hidden style={{ width: 12 }}>
                  {current ? "●" : ""}
                </span>
                {rowLabel}
              </button>
            );
          })}
          <div style={RULE} />
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              onChoose("chart");
            }}
            style={ROW}
          >
            <span aria-hidden style={{ width: 12 }} />
            Chart
          </button>
          {follow && (
            <>
              <div style={HEADING}>Follow — this whole layout</div>
              {[{ id: null, name: "Chart clicked last" }, ...follow.options].map(({ id, name }) => {
                const on = (id ?? undefined) === follow.current;
                return (
                  <button
                    key={id ?? "last"}
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      if (!on) follow.onFollow(id);
                    }}
                    style={{ ...ROW, ...(on ? ROW_ON : {}) }}
                  >
                    <span aria-hidden style={{ width: 12 }}>
                      {on ? "●" : ""}
                    </span>
                    {name}
                  </button>
                );
              })}
            </>
          )}
        </Menu>
      )}
      {config.view === "fundamentals" ? (
        <FinStGrainContext.Provider value={grainStore}>
          <div style={BODY}>{body}</div>
        </FinStGrainContext.Provider>
      ) : (
        <div style={BODY}>{body}</div>
      )}
    </div>
  );
}

const HOST: React.CSSProperties = {
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  background: "var(--qf-surface)",
  borderRadius: 4,
  overflow: "hidden",
};

const HEAD: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  padding: "3px 6px 3px 10px",
  borderBottom: "1px solid var(--qf-border)",
  background: "var(--qf-surface-alt)",
  flex: "0 0 auto",
};

const TITLE: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontSize: 12,
  fontWeight: 600,
  color: "var(--qf-text)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const ICON: React.CSSProperties = {
  width: 20,
  height: 20,
  padding: 0,
  border: "none",
  borderRadius: 3,
  background: "transparent",
  color: "var(--qf-text-secondary)",
  font: "inherit",
  fontSize: 12,
  cursor: "pointer",
};

const BODY: React.CSSProperties = { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" };

const NOTE: React.CSSProperties = { padding: 16, fontSize: 12, color: "var(--qf-text-muted)" };

const ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  width: "100%",
  font: "inherit",
  fontSize: 12,
  padding: "6px 10px",
  border: 0,
  background: "transparent",
  color: "var(--qf-text)",
  cursor: "pointer",
  textAlign: "left",
};

const ROW_ON: React.CSSProperties = { background: "var(--qf-selection)" };

const ROW_TAKEN: React.CSSProperties = { color: "var(--qf-text-muted)", cursor: "default", opacity: 0.55 };

const RULE: React.CSSProperties = { height: 1, margin: "4px 0", background: "var(--qf-border)" };

const HEADING: React.CSSProperties = {
  margin: "6px 0 2px",
  padding: "6px 10px 2px",
  borderTop: "1px solid var(--qf-border)",
  fontSize: 10.5,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  color: "var(--qf-text-muted)",
};
